import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Badge,
  Typography,
  useMediaQuery,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/landing/Plumerialogo-400x69.png";
import { authApi } from "../api";

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTabletOrUp = useMediaQuery(theme.breakpoints.up("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const [scrollPosition, setScrollPosition] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [searchText, setSearchText] = useState("");

  const handleSignOut = () => {
    authApi.signOut(); // Removes token from localStorage
    setIsAuthenticated(false);
    navigate("/");
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    // Dispatch custom event for real-time search
    window.dispatchEvent(new CustomEvent("productSearch", { detail: value }));
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsAuthenticated(!!token);
  }, [location]); // Check token on route change

  return (
    <Box
      sx={{
        borderBottom: "1px solid #eee",
        position: scrollPosition > 0 ? "sticky" : "static",
        top: 0,
        zIndex: 10,
        backgroundColor: scrollPosition > 0 ? "#fff" : "transparent",
        boxShadow: scrollPosition > 0 ? 2 : "none",
        transition: "all 0.3s ease-in-out",
      }}
    >
      {/* Top header row */}
      <Box
        sx={{
          py: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          width={{ xl: "95%", lg: "95%", md: "90%", sm: "90%", xs: "95%" }}
          margin="auto"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          {isMobile ? (
            <>
              <IconButton>
                <MenuIcon />
                {isTabletOrUp && "Menu"}
              </IconButton>
              <Box
                component="img"
                src={logo}
                alt="DurhamPlumeria Logo"
                sx={{ height: 40 }}
                onClick={() => navigate("/")}
              />
              <IconButton>
                <Badge badgeContent={0} color="success" showZero>
                  <ShoppingBagOutlinedIcon />
                </Badge>
              </IconButton>
            </>
          ) : (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width={{ xl: "95%", lg: "95%", md: "95%" }}
              margin="auto"
              px={2}
            >
              {/* Logo */}
              <Box
                component="img"
                src={logo}
                alt="DurhamPlumeria Logo"
                sx={{ height: { md: 45, xl: 50 }, cursor: "pointer" }}
                onClick={() => navigate("/")}
              />

              {/* Search bar - optional hide on scroll if you want */}
              {scrollPosition === 0 && location.pathname !== "/products" && (
                <Box
                  sx={{
                    mx: 4,
                    flexGrow: 1,
                    minWidth: { md: 300, lg: 400, xl: 500 },
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Search for products"
                    variant="outlined"
                    size="small"
                    value={searchText}
                    onChange={handleSearch}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
              )}

              {/* Auth buttons */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: { xl: 2, md: 1 },
                }}
              >
                {isAuthenticated ? (
                  <>
                    <Typography
                      variant="body2"
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate("/products")}
                    >
                      My Products
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSignOut}
                      sx={{
                        backgroundColor: "#31B44B",
                        "&:hover": { backgroundColor: "#2a9c3f" },
                      }}
                    >
                      Sign Out
                    </Button>
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate("/products-preview")}
                  >
                    Products
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Drawers remain unchanged (you already implemented them well) */}
      {/* ... Drawer code continues ... */}
    </Box>
  );
};

export default Header;
